import styled from '@emotion/styled'
import { ReactNode } from 'react'

import { ShiftActivity } from '@bemlo/enums'
import { shiftToQuarters } from '@bemlo/schedule'

import { Color } from '../../../../constants'
import { Button, Dialog, Flex, Paper, Text } from '../../../../elements'
import { Cell } from '../components/Cell'
import { Row } from '../components/Row'
import { UIShift } from '../types'
import { useSchedule } from '../useSchedule'

const Wrapper = styled.div({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
  width: '100%',
  fontWeight: 500,
  color: Color.CHARCOAL,
})

type Value = {
  x: number
  y: number
}

export type DayDemandGraphData = {
  date: string
  day: number
  min: Value[]
  max: Value[]
  fill: Value[]
  flex: Value[]
  shifts: UIShift[]
}

type DayDemandProps = {
  title?: string
  demands: {
    date: string
    demand:
      | {
          day: number
          scheduleResourceId: string
          min: number[]
          max: number[]
        }
      | undefined
  }[]
  dialogControls?: ReactNode
  dialogContent: ReactNode
  setGraphData: (data: DayDemandGraphData | undefined) => void
  graphData: DayDemandGraphData | undefined
  countRowsAsFilled?: boolean
  unitId: string
  scheduleResourceId: string | undefined
  size?: 'sm' | 'md'
}

export const DayDemand = (props: DayDemandProps) => {
  const { days, shifts: allShifts, isScrolled, zoom } = useSchedule()

  const handleSetGraphData = (dayIndex: number) => {
    const day = days[dayIndex]
    const shiftsOnDay = allShifts.filter(
      (shift) =>
        // Correct day
        shift.date === day.dateISO &&
        // Correct unit
        shift.unitId === props.unitId &&
        // Correct schedule resource if set
        (shift.scheduleResourceId === props.scheduleResourceId ||
          !props.scheduleResourceId),
    )
    const shiftsFilled = shiftsOnDay.filter(({ rowKey, bookedCandidateId }) => {
      if (props.countRowsAsFilled && typeof rowKey === 'number') return true
      return !!bookedCandidateId
    })

    const fill = Array.from({ length: 96 }, () => 0)
    const flex = Array.from({ length: 96 }, () => 0)

    shiftsFilled.forEach((booking) => {
      const slots = shiftToQuarters(booking)
      slots.forEach((value, slot) => {
        if (booking.activity === ShiftActivity.WORK) {
          fill[slot] += value
          flex[slot] += value
        }
        if (booking.activity === ShiftActivity.FLEX) {
          flex[slot] += value
        }
      })
    })

    const demand = props.demands?.find((d) => d.date === day.dateISO)?.demand
    const min = demand?.min || []
    const max = demand?.max || []

    props.setGraphData({
      date: day.dateISO,
      day: dayIndex,
      fill: fill.map((value, index) => ({
        x: index,
        y: value,
      })),
      flex: flex.map((value, index) => ({
        x: index,
        y: value,
      })),
      min: min.map((value, index) => ({
        x: index,
        y: value,
      })),
      max: max.map((value, index) => ({
        x: index,
        y: value,
      })),
      shifts: shiftsOnDay,
    })
  }

  const isSmall = props.size === 'sm'

  return (
    <Row>
      <Cell isTitle rowSpan={1} rightShadow={isScrolled}>
        {props.title ? (
          <Flex styles={{ height: '100%', alignItems: 'center', pl: 2 }}>
            <Text bold fontSize={13}>
              {props.title}
            </Text>
          </Flex>
        ) : null}
      </Cell>
      {days.map((day, dayIndex) => {
        return (
          <Cell
            key={day.dateISO}
            disabled={!day.withinRange}
            weekend={day.isWeekend}
          >
            <Wrapper>
              <Button
                size={isSmall ? 'xs' : 'sm'}
                design="transparent-light"
                icon="ChartBarIcon"
                onClick={() => handleSetGraphData(dayIndex)}
              >
                {zoom === 'week' ? 'Bemanning' : ''}
              </Button>
            </Wrapper>
          </Cell>
        )
      })}
      <Dialog
        isOpen={!!props.graphData}
        onClose={() => props.setGraphData(undefined)}
      >
        <Paper>
          <Flex col gap={3}>
            <Flex gap={2.5} items="center" styles={{ px: 2, py: 1.5 }}>
              <Text bold>{props.graphData?.date}</Text>
              <Button
                size="xs"
                design="white"
                icon="ChevronLeftIcon"
                disabled={props.graphData?.day === 0}
                onClick={() =>
                  handleSetGraphData((props.graphData?.day ?? days.length) - 1)
                }
              />
              <Button
                size="xs"
                design="white"
                icon="ChevronRightIcon"
                disabled={props.graphData?.day === days.length - 1}
                onClick={() =>
                  handleSetGraphData((props.graphData?.day ?? 0) + 1)
                }
                styles={{ mr: 'auto' }}
              />
              {props.dialogControls}
              <Button
                size="xs"
                design="transparent-light"
                icon="XIcon"
                onClick={() => props.setGraphData(undefined)}
              />
            </Flex>
            {props.dialogContent}
          </Flex>
        </Paper>
      </Dialog>
    </Row>
  )
}
